import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ErrorStrings, ValidationPatterns } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { PetRehomingRequest, PetGender } from 'src/app/interfaces/pet';
import { Species } from 'src/app/interfaces/species';
import { User } from 'src/app/interfaces/user';
import { PetRehomeSuccessModalComponent } from 'src/app/modals/pet-rehome-success-modal/pet-rehome-success-modal.component';
import { PetService } from 'src/app/services/pet/pet.service';
import { UserService } from 'src/app/services/user/user.service';
import { PerkService } from '../../../services/perk/perk.service';
import { FeatureFlagsService } from '../../../services/feature-flag/feature-flag.service';
import { environment } from '../../../../environments/environment';
import { PetplanService } from 'src/app/services/petplan/petplan.service';
import { ConfigService } from 'src/app/services/config/config.service';

@Component({
  selector: 'app-rehome-pet',
  templateUrl: './rehome-pet.component.html',
  styleUrls: ['./rehome-pet.component.scss'],
})
export class RehomePetComponent implements OnInit {
  user: User;
  @ViewChild('petBreedTypeahead') petBreedTypeahead: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  alert: Alert = null;
  errorStrings = ErrorStrings;
  submitButtonLoading = false;
  loadingSpecies = false;
  loadingBreeds = false;
  speciesList: Species[] = [];
  selectedSpecies: Species = null;
  breedNamesList: string[] = [];
  petId: number = null;

  public isPetPlanCheckboxChecked = false;
  public showPetPlanTextBox = false;

  pet: PetRehomingRequest = {
    name: null,
    species: null,
    breed: null,
    gender: null,
    chipNumber: null,
    extraInfo: null,
    vetCode: null,
    existingKeeper: {
      name: null,
      phoneNumber: null,
    },
  };
  petGender = PetGender;
  validationPatterns = ValidationPatterns;
  isBrowser: boolean;
  petPlanText1: string;
  petPlanText2: string;
  petPlanCheckboxLabel: string;
  useRebrandedLayout: boolean;

  constructor(
    private route: ActivatedRoute,
    private petService: PetService,
    private petplanService: PetplanService,
    private _modalService: NgbModal,
    private userService: UserService,
    private router: Router,
    private perkService: PerkService,
    private featureFlagService: FeatureFlagsService,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.petPlanText1 = this.configService.getTextFromKey('pet-details-page-petplan-text-1');
    this.petPlanCheckboxLabel = this.configService.getTextFromKey('pet-details-page-petplan-checkbox-label');
    this.petPlanText2 = this.configService.getTextFromKey('pet-details-page-petplan-text-2');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  ngOnInit() {
    if (this.userService.isUserBasicOrBasicPlus(this.user.productType)) {
      // Basic or basic plus users cannot access this page, because they need to pay to be able to request a transfer of keepership,
      // and after payment, users are automatically subscribed to alert membership.
      this.router.navigate(['dashboard']);
      return;
    }

    const queryParams = this.route.snapshot.queryParams;

    if (queryParams && queryParams.chipNumber && queryParams.chipNumber !== 'UNKNOWN_CHIP') {
      const chipNumber = queryParams.chipNumber.replace(/\s/g, '');

      this.pet.chipNumber = chipNumber;

      if (this.featureFlagService.isEnabled('isPetPlanFreeInsuranceAvailable')) {
        this.perkService
          .getPetEligibilityFromChipNumber(chipNumber, environment.eligibilityShortCode)
          .subscribe((res) => {
            this.showPetPlanTextBox = res.isEligible;
            this.petId = res.petId;
          });
      }
    } else {
      // If the chip number is not defined, we cannot proceed with the request
      // This can happen if the user navigates to this page by typing (incorrectly) the URL directly in the browser search bar,
      // or if the URL returned by the API is broken.
      this.router.navigate(['/404']);
    }

    if (queryParams && queryParams.vetCode && queryParams.vetCode !== 'UNKNOWN_VET') {
      this.pet.vetCode = queryParams.vetCode;
    }

    this.loadSpecies();
  }

  send(): void {
    this.closeAlert();
    this.submitButtonLoading = true;

    this.petService.sendPetRehomingRequest(this.pet).subscribe(
      () => {
        this.trackEvent('petrehomerequest::submitted');
        this.openRehomeSuccessModal();

        if (
          this.featureFlagService.isEnabled('isFreePetInsuranceCheckBoxAvailable') &&
          this.isPetPlanCheckboxChecked
        ) {
          this.petplanService.petplanSave(this.petId).subscribe();
        }
      },
      (err) => {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
      },
    );
  }

  public openRehomeSuccessModal() {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    };

    const petRehomeSuccessModalRef = this._modalService.open(
      PetRehomeSuccessModalComponent,
      modalOptions,
    );
    petRehomeSuccessModalRef.componentInstance.modalRef = petRehomeSuccessModalRef;
  }

  closeAlert() {
    this.alert = null;
  }

  updateBreedsList(): void {
    // Loading breeds list
    this.breedNamesList = [];
    this.loadingBreeds = true;
    this.pet.species = this.selectedSpecies.description;
    this.petService
      .getBreedsFor(this.selectedSpecies.venomCode)
      .subscribe(
        (breedsList) => {
          this.breedNamesList = Object.keys(breedsList);

          if (this.breedNamesList.length === 1) {
            this.pet.breed = this.breedNamesList[0];
          } else {
            this.pet.breed = null;
          }
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        },
      )
      .add(() => {
        this.loadingBreeds = false;
      });
  }

  searchBreed = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.petBreedTypeahead.isPopupOpen()),
    );
    const inputFocus$ = this.focus$;
    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => {
        if (term === '') {
          return this.breedNamesList.length <= 10 ? this.breedNamesList : [];
        } else {
          return this.breedNamesList
            .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
            .slice(0, 10);
        }
      }),
    );
  };

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  private loadSpecies(): void {
    // Loading species list
    this.loadingSpecies = true;
    this.petService
      .getAllSpecies()
      .subscribe(
        (speciesList) => {
          this.speciesList = speciesList;
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        },
      )
      .add(() => {
        this.loadingSpecies = false;
      });
  }

  public checkValue(checked: boolean) {
    this.isPetPlanCheckboxChecked = checked;
  }
}
